import type { AuthUser } from '@stayon/auth-core';
import { CalendarDays, ChartColumn, SprayCan, Users, type LucideIcon } from 'lucide-react';
import type { NavArea, NavEntry } from './adminNavigation';
import { settingsVisible, visibleAreas, visiblePlatformEntries } from './navVisibility';

export interface PhoneTab {
  key: string;
  label: string;
  icon: LucideIcon;
  entry: NavEntry;
  area: NavArea;
}

export interface PhoneNavigation {
  tabs: PhoneTab[];
  /** Was in „Mehr“ steht; leer, wenn alles in die Leiste passt. */
  moreAreas: NavArea[];
  settings: boolean;
  platform: NavEntry[];
}

const ENTRY_ICON: Record<string, LucideIcon> = {
  Aufgaben: SprayCan,
  Woche: CalendarDays,
  Auswertung: ChartColumn,
  Teams: Users,
};
const MAX_TABS = 4;

/**
 * Leiste unten je Rolle (Board „Gerüst · Handy“): hat jemand nur einen Bereich – die
 * Reinigungsrollen –, sind dessen Unterpunkte die Reiter; sonst je Bereich ein Reiter, und was
 * nicht in vier passt, steht samt Einstellungen und Plattform in „Mehr“.
 */
export function phoneNavigation(user: AuthUser | null): PhoneNavigation {
  const areas = visibleAreas(user);
  const settings = settingsVisible(user);
  const platform = visiblePlatformEntries(user);
  const single = areas.length === 1 ? areas[0] : undefined;
  if (single) {
    const tabs = single.entries.slice(0, MAX_TABS).map((entry) => ({
      key: `${entry.to}${JSON.stringify(entry.search ?? {})}`,
      label: entry.label,
      icon: ENTRY_ICON[entry.label] ?? single.icon,
      entry,
      area: single,
    }));
    return { tabs, moreAreas: [], settings, platform };
  }
  const fits = areas.length <= MAX_TABS && !settings && platform.length === 0;
  const shown = fits ? areas : areas.slice(0, MAX_TABS);
  return {
    tabs: shown.map((area) => ({
      key: area.id,
      label: area.label,
      icon: area.icon,
      entry: area.entries[0] as NavEntry,
      area,
    })),
    moreAreas: fits ? [] : areas.slice(MAX_TABS),
    settings,
    platform,
  };
}

export const needsMore = (nav: PhoneNavigation) =>
  nav.moreAreas.length > 0 || nav.settings || nav.platform.length > 0;
